import { useState, useEffect, useRef, type JSX } from 'react';
import { X } from 'lucide-react';
import ArbiSelect from '../ui/ArbiSelect';
import type { CancellationReason } from '../../types';

/**
 * CancelPOModal — Day 56 item 7+8.
 *
 * Captures (a) cancellation_reason from the bounded enum minus
 * 'replaced_by_revision' (reserved for chain_cancel_po auto-set on the parent
 * row), (b) free-text rationale required when reason='other', (c) optional
 * notify_distributor / notify_submitter runtime flags.
 *
 * Used by both the per-PO cancel path on PODetail and the bulk-cancel path on
 * PurchaseOrders. Bulk applies the same payload to every selected PO.
 */

const REASON_OPTIONS: { value: CancellationReason; label: string }[] = [
  { value: 'supplier_out_of_stock', label: 'Supplier out of stock' },
  { value: 'price_changed', label: 'Price changed' },
  { value: 'duplicate', label: 'Duplicate PO' },
  { value: 'error', label: 'PO error' },
  { value: 'customer_requested', label: 'Customer requested' },
  { value: 'credit_unavailable', label: 'Credit unavailable' },
  { value: 'approval_rejected', label: 'Approval rejected' },
  { value: 'other', label: 'Other (specify below)' },
];

export interface CancelPOPayload {
  reason: CancellationReason;
  otherText: string | null;
  notifyDistributor: boolean;
  notifySubmitter: boolean;
}

interface CancelPOModalProps {
  open: boolean;
  /** Display block at the top — PO number, distributor name, total. */
  poLabel: string;
  /** When true the distributor has no notification recipient (no dist_rep
   *  account); the notify_distributor checkbox is hidden. */
  hideNotifyDistributor?: boolean;
  /** When true the current user IS the PO submitter; the notify_submitter
   *  checkbox is hidden. */
  hideNotifySubmitter?: boolean;
  /** Distinct testid for the bulk variant so e2e selectors don't collide
   *  with the per-PO modal. */
  testIdPrefix?: string;
  onConfirm: (payload: CancelPOPayload) => void;
  onCancel: () => void;
}

export default function CancelPOModal({
  open,
  poLabel,
  hideNotifyDistributor = false,
  hideNotifySubmitter = false,
  testIdPrefix = 'cancel-po',
  onConfirm,
  onCancel,
}: CancelPOModalProps): JSX.Element | null {
  const [reason, setReason] = useState<CancellationReason | null>(null);
  const [otherText, setOtherText] = useState<string>('');
  const [notifyDistributor, setNotifyDistributor] = useState<boolean>(true);
  const [notifySubmitter, setNotifySubmitter] = useState<boolean>(true);
  const reasonRef = useRef<HTMLDivElement | null>(null);

  // Reset on every open (don't carry stale state across PO contexts).
  useEffect(() => {
    if (open) {
      setReason(null);
      setOtherText('');
      setNotifyDistributor(true);
      setNotifySubmitter(true);
    }
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const otherInvalid = reason === 'other' && otherText.trim().length === 0;
  const submitDisabled = reason === null || otherInvalid;

  const handleConfirm = (): void => {
    if (reason === null || otherInvalid) return;
    onConfirm({
      reason,
      otherText: reason === 'other' ? otherText.trim() : null,
      notifyDistributor,
      notifySubmitter,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Cancel PO"
      data-testid={`${testIdPrefix}-modal`}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onCancel}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative bg-surface-2 border border-border rounded-lg shadow-xl w-[420px] p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-bright">Cancel PO</h3>
            <p className="text-2xs text-ink-muted mt-0.5">{poLabel}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close cancel modal"
            className="text-ink-muted hover:text-ink-primary transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div ref={reasonRef}>
          <label
            htmlFor={`${testIdPrefix}-reason`}
            className="text-2xs text-ink-muted font-medium mb-1 block"
          >
            Reason for cancellation <span className="text-danger">*</span>
          </label>
          <div id={`${testIdPrefix}-reason`} data-testid={`${testIdPrefix}-reason`}>
            <ArbiSelect<CancellationReason>
              value={reason ?? undefined}
              options={REASON_OPTIONS}
              onChange={(v) => setReason(v)}
              placeholder="Select a reason…"
            />
          </div>
        </div>

        {reason === 'other' && (
          <div>
            <label
              htmlFor={`${testIdPrefix}-other-text`}
              className="text-2xs text-ink-muted font-medium mb-1 block"
            >
              Specify reason <span className="text-danger">*</span>
            </label>
            <textarea
              id={`${testIdPrefix}-other-text`}
              data-testid={`${testIdPrefix}-other-text`}
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              rows={3}
              className="arbi-input text-xs w-full resize-none"
              placeholder="Brief rationale"
            />
            {otherInvalid && (
              <p className="text-2xs text-danger mt-1">
                Reason text is required for &quot;Other&quot;
              </p>
            )}
          </div>
        )}

        {(!hideNotifyDistributor || !hideNotifySubmitter) && (
          <div className="space-y-2 pt-1 border-t border-border-subtle">
            <div className="text-2xs uppercase tracking-wider text-ink-muted pt-2">
              Notifications
            </div>
            {!hideNotifyDistributor && (
              <label className="flex items-center gap-2 text-xs text-ink-secondary cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`${testIdPrefix}-notify-distributor`}
                  checked={notifyDistributor}
                  onChange={(e) => setNotifyDistributor(e.target.checked)}
                  className="accent-accent"
                />
                Notify distributor rep
              </label>
            )}
            {!hideNotifySubmitter && (
              <label className="flex items-center gap-2 text-xs text-ink-secondary cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`${testIdPrefix}-notify-submitter`}
                  checked={notifySubmitter}
                  onChange={(e) => setNotifySubmitter(e.target.checked)}
                  className="accent-accent"
                />
                Notify PO submitter
              </label>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            data-testid={`${testIdPrefix}-cancel`}
            className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            data-testid={`${testIdPrefix}-confirm`}
            disabled={submitDisabled}
            className="arbi-btn text-xs bg-danger text-white hover:bg-danger/80 px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel PO
          </button>
        </div>
      </div>
    </div>
  );
}
